import {
  DEFAULT_STORE_LOCATIONS,
  DEFAULT_STORE_PROXIMITY_RADIUS_METERS,
  FORCE_NEAR_STORE_FOR_TESTING,
  GEOLOCATION_TIMEOUT_MS,
  STORE_LOCATIONS_API_URL,
  USER_LOCATION_CACHE_TTL_MS,
} from "../../config/storeLocationsConfig";

const SESSION_POSITION_KEY = "orbiee_user_position_v1";
const SESSION_PROXIMITY_KEY = "orbiee_nearby_store_v1";

let storeListCache = null;
let storeListCacheExpiry = 0;
const STORE_LIST_CACHE_TTL_MS = 15 * 60 * 1000;

const EARTH_RADIUS_METERS = 6_371_000;

/** Latest resolved proximity — available synchronously after background tracking completes. */
let proximitySnapshot = null;

/** In-flight or completed background resolution promise. */
let proximityPromise = null;

let trackingStarted = false;

const toRadians = (degrees) => (degrees * Math.PI) / 180;

export function distanceMetersBetween(lat1, lon1, lat2, lon2) {
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_METERS * c;
}

function readSessionJson(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeSessionJson(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore quota / private browsing errors.
  }
}

function isFreshTimestamp(timestamp, ttlMs = USER_LOCATION_CACHE_TTL_MS) {
  return typeof timestamp === "number" && Date.now() - timestamp < ttlMs;
}

function normalizeStore(store) {
  if (!store || typeof store !== "object") return null;
  const latitude = Number(store.latitude);
  const longitude = Number(store.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  return {
    id: String(store.id ?? "").trim() || "unknown-store",
    name: String(store.name ?? "our store").trim() || "our store",
    latitude,
    longitude,
    proximityRadiusMeters:
      Number(store.proximityRadiusMeters) > 0
        ? Number(store.proximityRadiusMeters)
        : DEFAULT_STORE_PROXIMITY_RADIUS_METERS,
  };
}

export async function getStoreLocations() {
  if (storeListCache && Date.now() < storeListCacheExpiry) {
    return storeListCache;
  }

  if (STORE_LOCATIONS_API_URL) {
    try {
      const response = await fetch(STORE_LOCATIONS_API_URL, {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      if (response.ok) {
        const payload = await response.json();
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.stores)
            ? payload.stores
            : [];
        const normalized = list.map(normalizeStore).filter(Boolean);
        if (normalized.length) {
          storeListCache = normalized;
          storeListCacheExpiry = Date.now() + STORE_LIST_CACHE_TTL_MS;
          return storeListCache;
        }
      }
    } catch (error) {
      console.warn("[location] Failed to fetch store locations:", error);
    }
  }

  storeListCache = DEFAULT_STORE_LOCATIONS.map(normalizeStore).filter(Boolean);
  storeListCacheExpiry = Date.now() + STORE_LIST_CACHE_TTL_MS;
  return storeListCache;
}

export function findNearestStoreInRange(userLat, userLng, stores) {
  let nearest = null;

  for (const store of stores) {
    const distanceMeters = distanceMetersBetween(
      userLat,
      userLng,
      store.latitude,
      store.longitude
    );
    if (distanceMeters > store.proximityRadiusMeters) continue;

    if (!nearest || distanceMeters < nearest.distanceMeters) {
      nearest = { store, distanceMeters };
    }
  }

  return nearest;
}

function getBrowserGeolocation() {
  return new Promise((resolve, reject) => {
    if (!navigator?.geolocation) {
      reject(new Error("Geolocation is not supported by this browser."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMeters: position.coords.accuracy,
        });
      },
      (error) => reject(error),
      {
        enableHighAccuracy: false,
        timeout: GEOLOCATION_TIMEOUT_MS,
        maximumAge: 120_000,
      }
    );
  });
}

async function resolveUserPosition() {
  const cached = readSessionJson(SESSION_POSITION_KEY);
  if (cached?.latitude != null && isFreshTimestamp(cached.timestamp)) {
    return { ...cached, source: "cache" };
  }

  if (FORCE_NEAR_STORE_FOR_TESTING) {
    const stores = await getStoreLocations();
    const testStore = stores[0];
    if (testStore) {
      console.info(
        "[location] TEST MODE: simulating user at store",
        testStore.name
      );
      const position = {
        latitude: testStore.latitude,
        longitude: testStore.longitude,
        accuracyMeters: 5,
        timestamp: Date.now(),
        source: "test_override",
      };
      writeSessionJson(SESSION_POSITION_KEY, position);
      return position;
    }
  }

  try {
    const position = await getBrowserGeolocation();
    const payload = {
      ...position,
      timestamp: Date.now(),
      source: "geolocation",
    };
    writeSessionJson(SESSION_POSITION_KEY, payload);
    console.info(
      "[location] Using live GPS:",
      payload.latitude,
      payload.longitude,
      `(±${Math.round(payload.accuracyMeters)}m)`
    );
    return payload;
  } catch (error) {
    console.warn("[location] Geolocation unavailable:", error?.message || error);
    return null;
  }
}

async function computeStoreProximity() {
  const stores = await getStoreLocations();
  if (!stores.length) {
    return {
      isNearStore: false,
      store: null,
      distanceMeters: null,
      userPosition: null,
      source: "no_stores",
      timestamp: Date.now(),
      ready: true,
    };
  }

  const userPosition = await resolveUserPosition();
  if (!userPosition) {
    return {
      isNearStore: false,
      store: null,
      distanceMeters: null,
      userPosition: null,
      source: "unavailable",
      timestamp: Date.now(),
      ready: true,
    };
  }

  const match = findNearestStoreInRange(
    userPosition.latitude,
    userPosition.longitude,
    stores
  );

  const result = {
    isNearStore: Boolean(match),
    store: match?.store ?? null,
    distanceMeters: match?.distanceMeters ?? null,
    userPosition,
    source: userPosition.source || "geolocation",
    timestamp: Date.now(),
    ready: true,
  };

  if (match) {
    console.info(
      "[location] Near store:",
      match.store.name,
      `(${Math.round(match.distanceMeters)}m away)`
    );
  } else if (userPosition.source === "geolocation") {
    const nearest = stores
      .map((store) => ({
        store,
        distanceMeters: distanceMetersBetween(
          userPosition.latitude,
          userPosition.longitude,
          store.latitude,
          store.longitude
        ),
      }))
      .sort((a, b) => a.distanceMeters - b.distanceMeters)[0];
    if (nearest) {
      console.info(
        "[location] Not within range. Nearest store:",
        nearest.store.name,
        `(${Math.round(nearest.distanceMeters)}m away, limit ${nearest.store.proximityRadiusMeters}m)`
      );
    }
  }

  writeSessionJson(SESSION_PROXIMITY_KEY, result);
  writeSessionJson(SESSION_POSITION_KEY, {
    latitude: userPosition.latitude,
    longitude: userPosition.longitude,
    accuracyMeters: userPosition.accuracyMeters,
    timestamp: Date.now(),
    source: userPosition.source,
  });

  return result;
}

function hydrateFromSessionCache() {
  const cached = readSessionJson(SESSION_PROXIMITY_KEY);
  if (
    cached &&
    isFreshTimestamp(cached.timestamp) &&
    typeof cached.isNearStore === "boolean"
  ) {
    proximitySnapshot = { ...cached, ready: true };
    return true;
  }
  return false;
}

/**
 * Start location tracking as soon as the site loads (before login).
 * Safe to call multiple times — only runs once per page load.
 */
export function initLocationTracking() {
  if (trackingStarted) return proximityPromise;
  trackingStarted = true;

  hydrateFromSessionCache();

  // Defer GPS so app bootstrap / avatar WebRTC are not competing on first paint.
  proximityPromise = new Promise((resolve) => {
    const start = () => {
      computeStoreProximity()
        .then(resolve)
        .catch((error) => {
          console.warn("[location] Tracking failed:", error);
          resolve({
            isNearStore: false,
            store: null,
            distanceMeters: null,
            userPosition: null,
            source: "error",
            timestamp: Date.now(),
            ready: true,
          });
        });
    };

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      window.requestIdleCallback(start, { timeout: 2000 });
    } else {
      setTimeout(start, 0);
    }
  })
    .then((result) => {
      proximitySnapshot = result;
      return result;
    });

  return proximityPromise;
}

/**
 * Synchronous read of the pre-resolved store proximity decision.
 * Used at login so avatar startup is not blocked by geolocation.
 *
 * Returns null only if tracking has not finished yet and no fresh cache exists.
 */
export function getStoreProximitySnapshot() {
  if (proximitySnapshot?.ready) return proximitySnapshot;

  if (hydrateFromSessionCache()) return proximitySnapshot;

  return null;
}

/**
 * Returns whether the user should get the in-store welcome (sync).
 * Falls back to false if proximity is not ready yet.
 */
export function isUserNearStore() {
  const snapshot = getStoreProximitySnapshot();
  return Boolean(snapshot?.isNearStore && snapshot?.store);
}

/**
 * Optional: await background tracking (not used by avatar login flow).
 */
export function waitForStoreProximity() {
  if (!trackingStarted) initLocationTracking();
  return proximityPromise;
}
